import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create API instance with base URL
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Store REG code to User ID mapping
const regCodeToUserIdMap = {};

/**
 * Register a new user with a QR code
 * @param {string} qrCode - The registration QR code
 * @returns {Promise<Object>} - User data including userId and token
 */
export const registerUser = async (qrCode) => {
  try {
    const response = await authApi.post('/api/register', { qrCode });
    
    // Store the mapping from registration code to user ID
    if (qrCode.startsWith('REG-') && response.data && response.data.userId) {
      regCodeToUserIdMap[qrCode] = response.data.userId;
      // Also store in localStorage for persistence across sessions
      try {
        const storedMappings = JSON.parse(localStorage.getItem('regCodeMappings') || '{}');
        storedMappings[qrCode] = response.data.userId;
        localStorage.setItem('regCodeMappings', JSON.stringify(storedMappings));
      } catch (e) {
        console.error('Failed to store registration code mapping:', e);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
    throw new Error(errorMessage);
  }
};

export const loginWithCredentials = async (username, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/login/credentials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Complete user profile setup
export const completeUserProfile = async (userId, profileData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        username: profileData.username,
        password: profileData.password,
        profilePicture: profileData.profilePicture
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Profile update failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Profile update error:', error);
    throw error;
  }
};

// Check profile completion status
export const checkProfileStatus = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/status/${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check profile status');
    }

    return await response.json();
  } catch (error) {
    console.error('Profile status check error:', error);
    throw error;
  }
};

// Get user profile data
export const getUserProfile = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/${userId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get profile');
    }

    return await response.json();
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

// Update profile picture
export const updateProfilePicture = async (userId, profilePicture) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/picture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, profilePicture }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update profile picture');
    }

    return await response.json();
  } catch (error) {
    console.error('Update profile picture error:', error);
    throw error;
  }
};

/**
 * Process a QR code scan (registration or bonus)
 * @param {string|null} userId - User ID (null for registration)
 * @param {string} qrCode - The scanned QR code
 * @param {string} type - Type of QR code ('REGISTRATION' or 'BONUS')
 * @returns {Promise<Object>} - Result of the scan operation
 */
export const scanQrCode = async (userId, qrCode, type) => {
  try {
    if (type === 'REGISTRATION') {
      return await registerUser(qrCode);
    } else if (type === 'BONUS') {
      const response = await authApi.post('/api/scan', { userId, qrCode });
      return response.data;
    } else {
      throw new Error('Invalid QR code type');
    }
  } catch (error) {
    console.error('QR scan error:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to process QR code. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Login with a User ID or registration code
 * @param {string} userId - The user ID or registration code to attempt login with
 * @returns {Promise<Object>} - Login result with userId and token if successful
 */
export const loginWithUserID = async (userId) => {
  try {
    // Try to load any stored mappings from localStorage
    try {
      const storedMappings = JSON.parse(localStorage.getItem('regCodeMappings') || '{}');
      Object.assign(regCodeToUserIdMap, storedMappings);
    } catch (e) {
      console.error('Failed to load stored registration code mappings:', e);
    }
    
    // Check if this is a registration code we've seen before
    if (userId.startsWith('REG-') && regCodeToUserIdMap[userId]) {
      console.log('Using cached user ID for registration code:', userId);
      // We know the actual user ID for this registration code
      userId = regCodeToUserIdMap[userId];
    }
    
    const response = await authApi.post('/api/login', { userId });
    
    // If we successfully logged in with a registration code, store the mapping
    if (response.data && response.data.userId && userId.startsWith('REG-')) {
      regCodeToUserIdMap[userId] = response.data.userId;
      // Also store in localStorage
      try {
        const storedMappings = JSON.parse(localStorage.getItem('regCodeMappings') || '{}');
        storedMappings[userId] = response.data.userId;
        localStorage.setItem('regCodeMappings', JSON.stringify(storedMappings));
      } catch (e) {
        console.error('Failed to store registration code mapping:', e);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Validate a user session with token
 * @param {string} userId - User ID
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - User data if valid
 */
export const validateUserSession = async (userId, token) => {
  // Early validation - don't make API call with invalid data
  if (!userId || !token) {
    throw new Error('Invalid session data');
  }

  try {
    const response = await authApi.post('/api/validate', { userId, token });
    return response.data;
  } catch (error) {
    console.error('Session validation error:', error);

    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Session validation failed.';
    throw new Error(errorMessage);
  }
};

/**
 * Link a jacket to a user account
 * @param {string} userId - User ID
 * @param {string} jacketId - Jacket identifier
 * @returns {Promise<Object>} - Link result
 */
export const linkJacket = async (userId, jacketId) => {
  try {
    const response = await authApi.post('/api/jackets/link', { userId, jacketId });
    return response.data;
  } catch (error) {
    console.error('Jacket linking error:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to link jacket. Please try again.';
    throw new Error(errorMessage);
  }
};